import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Send, Paperclip, Check, X, ShieldCheck, MailCheck } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, REMINDER_STATUS_TONE } from "./billingMeta";

// Mode: "compose" (from invoice) | "review" (existing reminder)
export default function PaymentReminderModal({ open, onOpenChange, invoice, reminder, currentUser, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [prepared, setPrepared] = useState(reminder || null);
  const [form, setForm] = useState({ subject: "", body: "", attach_invoice: true });

  const isReview = !!prepared;

  useEffect(() => {
    if (!open) return;
    if (reminder) {
      setPrepared(reminder);
      setForm({ subject: reminder.subject, body: reminder.body, attach_invoice: reminder.attach_invoice });
    } else if (invoice) {
      // Prefill locally so user can preview before submitting
      const outstanding = Number(invoice.amount) - Number(invoice.amount_paid || 0);
      const subject = `Payment reminder — Invoice ${invoice.invoice_number} (${fmtMoney(outstanding)} outstanding)`;
      const body =
        `Dear ${invoice.client} team,\n\n` +
        `I hope this message finds you well. This is a friendly reminder from ISOQuest regarding the following invoice:\n\n` +
        `    Invoice number : ${invoice.invoice_number}\n` +
        `    Invoice date   : ${invoice.invoice_date}\n` +
        `    Description    : ${invoice.description || "ISOQuest services"}\n` +
        `    Amount due     : ${fmtMoney(outstanding)}\n` +
        `    Due date       : ${invoice.due_date}\n\n` +
        `Our records indicate that this amount is currently outstanding. If payment has already been arranged, ` +
        `please disregard this notice. Otherwise, we would greatly appreciate your prompt attention to this invoice.\n\n` +
        `A copy of the invoice is attached for your convenience. Please don't hesitate to reach out if you have any questions.\n\n` +
        `Kind regards,\n${currentUser?.name || "ISOQuest Team"}\nISOQuest Team\naccounts@isoquest.com`;
      setPrepared(null);
      setForm({ subject, body, attach_invoice: true });
    }
  }, [open, invoice, reminder, currentUser]);

  const outstanding = useMemo(() => {
    if (prepared) return prepared.outstanding_amount;
    if (invoice) return Number(invoice.amount) - Number(invoice.amount_paid || 0);
    return 0;
  }, [prepared, invoice]);

  const submitForApproval = async () => {
    if (!invoice) return;
    setSaving(true);
    try {
      const rem = await endpoints.prepareReminder({
        invoice_id: invoice.id,
        prepared_by: currentUser?.name || "Staff",
        subject: form.subject,
        body: form.body,
        attach_invoice: form.attach_invoice,
      });
      toast.success("Reminder submitted for approval");
      setPrepared(rem);
      onChanged?.();
    } catch { toast.error("Could not submit for approval"); } finally { setSaving(false); }
  };

  const saveEdits = async () => {
    if (!prepared) return;
    setSaving(true);
    try {
      const rem = await endpoints.updateReminder(prepared.id, {
        invoice_id: prepared.invoice_id,
        prepared_by: prepared.prepared_by,
        subject: form.subject,
        body: form.body,
        attach_invoice: form.attach_invoice,
      });
      toast.success("Draft saved");
      setPrepared(rem);
      onChanged?.();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const approveSend = async () => {
    if (!prepared) return;
    setSaving(true);
    try {
      await endpoints.approveAndSendReminder(prepared.id, { approved_by: currentUser?.name || "Staff" });
      toast.success(`Reminder sent to ${prepared.client}`);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!prepared) return;
    setSaving(true);
    try {
      await endpoints.cancelReminder(prepared.id);
      toast.success("Reminder cancelled");
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error("Could not cancel"); } finally { setSaving(false); }
  };

  const canApprove = prepared && prepared.status === "Pending Approval" && prepared.prepared_by !== (currentUser?.name || "");
  const selfApproveWarning = prepared && prepared.status === "Pending Approval" && prepared.prepared_by === (currentUser?.name || "");
  const sent = prepared && prepared.status === "Sent";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-payment-reminder" className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <MailCheck className="h-4.5 w-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle>{isReview ? "Review payment reminder" : "Compose payment reminder"}</DialogTitle>
              <DialogDescription>
                {isReview ? "A colleague must review and approve before the email is sent to the client." : "Prepare a professional email. It will require staff approval before sending."}
              </DialogDescription>
            </div>
            {prepared && (
              <Badge variant="outline" className={`rounded-full ${REMINDER_STATUS_TONE[prepared.status]}`}>{prepared.status}</Badge>
            )}
          </div>
        </DialogHeader>

        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-slate-900/40 p-3 text-xs grid grid-cols-2 gap-2">
          <div><span className="text-slate-500">Client:</span> <span className="font-semibold">{invoice?.client || prepared?.client}</span></div>
          <div><span className="text-slate-500">Invoice:</span> <span className="font-mono font-semibold">{invoice?.invoice_number || prepared?.invoice_number}</span></div>
          <div><span className="text-slate-500">Amount outstanding:</span> <span className="font-mono font-bold text-rose-600">{fmtMoney(outstanding)}</span></div>
          <div><span className="text-slate-500">Due date:</span> <span className="font-semibold">{invoice?.due_date || prepared?.due_date}</span></div>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="rem-subject">Subject</Label>
            <Input
              data-testid="reminder-subject-input"
              id="rem-subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              disabled={sent}
            />
          </div>
          <div>
            <Label htmlFor="rem-body">Email body</Label>
            <Textarea
              data-testid="reminder-body-input"
              id="rem-body"
              rows={12}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              className="font-mono text-xs"
              disabled={sent}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 px-3 py-2.5">
            <div className="flex items-center gap-2 text-sm">
              <Paperclip className="h-4 w-4 text-indigo-600" />
              <div>
                <div className="font-semibold">Attach invoice PDF</div>
                <div className="text-[11px] text-slate-500">A PDF copy of {invoice?.invoice_number || prepared?.invoice_number} will be attached to the email</div>
              </div>
            </div>
            <Switch data-testid="reminder-attach-switch" checked={!!form.attach_invoice} onCheckedChange={(v) => setForm((f) => ({ ...f, attach_invoice: v }))} disabled={sent} />
          </div>

          {selfApproveWarning && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/25 text-amber-800 dark:text-amber-200 text-xs px-3 py-2 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              You prepared this reminder — another staff member must approve and send it.
            </div>
          )}
          {prepared?.approved_by && (
            <div className="text-[11px] text-slate-500">
              Prepared by <span className="font-semibold">{prepared.prepared_by}</span>
              {prepared.approved_by && <> · approved & sent by <span className="font-semibold">{prepared.approved_by}</span></>}
              {prepared.sent_at && <> · {new Date(prepared.sent_at).toLocaleString()}</>}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {!prepared && (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="reminder-submit-approval" className="brand-gradient text-white" onClick={submitForApproval} disabled={saving}>
                <ShieldCheck className="h-4 w-4 mr-1" /> Submit for approval
              </Button>
            </>
          )}
          {prepared && prepared.status === "Pending Approval" && (
            <>
              <Button data-testid="reminder-cancel-button" variant="ghost" className="text-rose-600 mr-auto" onClick={cancel} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> Cancel reminder
              </Button>
              <Button data-testid="reminder-save-draft" variant="outline" onClick={saveEdits} disabled={saving}>Save draft</Button>
              {canApprove ? (
                <Button data-testid="reminder-approve-send" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={approveSend} disabled={saving}>
                  <Send className="h-4 w-4 mr-1" /> Approve & send
                </Button>
              ) : (
                <Button data-testid="reminder-approve-send-disabled" disabled className="bg-slate-200 text-slate-500">
                  <Check className="h-4 w-4 mr-1" /> Needs a second reviewer
                </Button>
              )}
            </>
          )}
          {sent && (
            <Button className="ml-auto" onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
